// Plik: frontend/src/pages/admin/SlotManagement.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Button, IconButton,
  Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText,
  TextField, CircularProgress, Alert, Grid, Card, CardContent,
  Divider, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import plLocale from 'date-fns/locale/pl';
import { format, isSaturday, isSunday, parseISO } from 'date-fns';
import { adminService } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';

const SlotManagement = () => {
  const [slots, setSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Stan dla dialogów
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentSlot, setCurrentSlot] = useState(null);
  
  // Stan dla formularzy
  const [newSlotData, setNewSlotData] = useState({
    date: null,
    time: '',
    capacity: 20
  });
  
  const [editSlotData, setEditSlotData] = useState({
    capacity: 20
  });
  
  // Pobierz terminy przy ładowaniu
  useEffect(() => {
    fetchSlots();
  }, []);
  
  // Funkcja pobierająca terminy
  const fetchSlots = async () => {
    setLoading(true);
    setError(null);
    
    const response = await adminService.getSlots();
    
    if (response.success) {
      // Grupowanie według daty
      const slotsGroupedByDate = response.data.reduce((acc, slot) => {
        if (!acc[slot.date]) {
          acc[slot.date] = [];
        }
        acc[slot.date].push(slot);
        return acc;
      }, {});
      
      // Sortowanie dat
      const sortedDates = Object.keys(slotsGroupedByDate).sort();
      
      // Przygotowanie posortowanych danych
      const sortedSlots = {};
      sortedDates.forEach(date => {
        sortedSlots[date] = slotsGroupedByDate[date].sort((a, b) => a.time.localeCompare(b.time));
      });
      
      setSlots(sortedSlots);
    } else {
      setError(response.error);
      toast.error('Nie udało się pobrać terminów');
    }
    
    setLoading(false);
  };
  
  // Obsługa zmiany danych nowego terminu
  const handleNewSlotChange = (field, value) => {
    setNewSlotData({
      ...newSlotData,
      [field]: value
    });
  };
  
  // Obsługa zmiany danych edytowanego terminu
  const handleEditSlotChange = (field, value) => {
    setEditSlotData({
      ...editSlotData,
      [field]: value
    });
  };
  
  // Dodaj nowy termin
  const handleAddSlot = async () => {
    // Walidacja
    if (!newSlotData.date || !newSlotData.time || !newSlotData.capacity) {
      toast.error('Wszystkie pola są wymagane');
      return;
    }
    
    const dateObj = new Date(newSlotData.date);
    if (!(isSaturday(dateObj) || isSunday(dateObj))) {
      toast.error('Termin musi być w weekend (sobota lub niedziela)');
      return;
    }
    
    const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timePattern.test(newSlotData.time)) {
      toast.error('Nieprawidłowy format czasu (HH:MM)');
      return;
    }
    
    if (parseInt(newSlotData.capacity) <= 0) {
      toast.error('Pojemność musi być większa od zera');
      return;
    }
    
    // Konwersja daty do formatu oczekiwanego przez API
    const formattedDate = format(dateObj, 'yyyy-MM-dd');
    
    setLoading(true);
    
    const response = await adminService.addSlot({
      date: formattedDate,
      time: newSlotData.time,
      capacity: parseInt(newSlotData.capacity)
    });
    
    if (response.success) {
      toast.success('Termin został dodany');
      setAddDialogOpen(false);
      // Resetuj formularz
      setNewSlotData({
        date: null,
        time: '',
        capacity: 20
      });
      // Odśwież listę
      fetchSlots();
    } else {
      toast.error(response.error || 'Nie udało się dodać terminu');
    }
    
    setLoading(false);
  };
  
  // Otwórz dialog edycji terminu
  const handleEditClick = (slot) => {
    setCurrentSlot(slot);
    setEditSlotData({
      capacity: slot.capacity
    });
    setEditDialogOpen(true);
  };
  
  // Otwórz dialog usuwania terminu
  const handleDeleteClick = (slot) => {
    setCurrentSlot(slot);
    setDeleteDialogOpen(true);
  };
  
  // Zapisz edytowany termin
  const handleSaveEdit = async () => {
    // Walidacja
    if (!editSlotData.capacity || parseInt(editSlotData.capacity) <= 0) {
      toast.error('Pojemność musi być większa od zera');
      return;
    }
    
    // Sprawdź, czy pojemność jest mniejsza niż liczba już zarejestrowanych uczestników
    if (parseInt(editSlotData.capacity) < currentSlot.registered) {
      toast.error(`Pojemność nie może być mniejsza niż liczba zarejestrowanych uczestników (${currentSlot.registered})`);
      return;
    }
    
    setLoading(true);
    
    const response = await adminService.updateSlot(currentSlot.id, {
      capacity: parseInt(editSlotData.capacity)
    });
    
    if (response.success) {
      toast.success('Termin został zaktualizowany');
      setEditDialogOpen(false);
      // Odśwież listę
      fetchSlots();
    } else {
      toast.error(response.error || 'Nie udało się zaktualizować terminu');
    }
    
    setLoading(false);
  };
  
  // Usuń termin
  const handleDeleteConfirm = async () => {
    setLoading(true);
    
    const response = await adminService.deleteSlot(currentSlot.id);
    
    if (response.success) {
      toast.success('Termin został usunięty');
      setDeleteDialogOpen(false);
      // Odśwież listę
      fetchSlots();
    } else {
      toast.error(response.error || 'Nie udało się usunąć terminu');
    }
    
    setLoading(false);
  };
  

  // Formatuj datę dla wyświetlenia
  const formatDate = (dateString) => {
    const date = parseISO(dateString);
    return format(date, 'EEEE, d MMMM yyyy', { locale: plLocale });
  };
  
  // Renderuj karty terminów dla każdego dnia
  const renderDateCards = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }
    
    if (Object.keys(slots).length === 0) {
      return (
        <Alert severity="info" sx={{ my: 2 }}>
          Brak dostępnych terminów. Dodaj nowy termin.
        </Alert>
      );
    }
    
    return Object.entries(slots).map(([date, dateSlots]) => (
      <Card key={date} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {formatDate(date)}
          </Typography>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Godzina</TableCell>
                  <TableCell>Pojemność</TableCell>
                  <TableCell>Zapisanych</TableCell>
                  <TableCell>Dostępnych miejsc</TableCell>
                  <TableCell>Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dateSlots.map((slot) => (
                  <TableRow key={slot.id}>
                    <TableCell>{slot.time}</TableCell>
                    <TableCell>{slot.capacity}</TableCell>
                    <TableCell>{slot.registered}</TableCell>
                    <TableCell>{slot.available_spots}</TableCell>
                    <TableCell>
                      <IconButton 
                        size="small"
                        onClick={() => handleEditClick(slot)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      
                      <Tooltip title={slot.registered > 0 ? "Nie można usunąć terminu z zapisanymi uczestnikami" : ""}>
                        <span>
                          <IconButton 
                            size="small"
                            onClick={() => handleDeleteClick(slot)}
                            disabled={slot.registered > 0}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    ));
  };
  
  return (
    <AdminLayout title="Zarządzanie Terminami">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Zarządzanie Terminami
        </Typography>
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Dodaj Termin
        </Button>
      </Box>
      
      {renderDateCards()}
      
      {/* Dialog dodawania nowego terminu */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>Dodaj nowy termin</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={plLocale}>
              <DatePicker
                label="Data (tylko weekendy)"
                value={newSlotData.date}
                onChange={(date) => handleNewSlotChange('date', date)}
                format="dd/MM/yyyy"
                shouldDisableDate={(date) => !(isSaturday(date) || isSunday(date))}
                sx={{ width: '100%', mb: 2, mt: 1 }}
              />
            </LocalizationProvider>
            
            <TextField
              label="Godzina"
              type="time"
              fullWidth
              value={newSlotData.time}
              onChange={(e) => handleNewSlotChange('time', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                step: 300, // 5 min
              }}
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="Limit uczestników"
              type="number"
              fullWidth
              value={newSlotData.capacity}
              onChange={(e) => handleNewSlotChange('capacity', e.target.value)}
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Anuluj</Button>
          <Button 
            onClick={handleAddSlot}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Dodaj'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog edycji terminu */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edytuj termin</DialogTitle>
        <DialogContent>
          {currentSlot && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                {formatDate(currentSlot.date)}, {currentSlot.time}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Obecnie zapisanych: {currentSlot.registered}
              </Typography>
              
              <TextField
                label="Limit uczestników"
                type="number"
                fullWidth
                value={editSlotData.capacity}
                onChange={(e) => handleEditSlotChange('capacity', e.target.value)}
                InputProps={{ 
                  inputProps: { 
                    min: Math.max(1, currentSlot.registered) 
                  } 
                }}
                helperText={`Minimalna wartość: ${currentSlot.registered}`}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Anuluj</Button>
          <Button 
            onClick={handleSaveEdit}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Zapisz zmiany'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog usuwania terminu */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Potwierdź usunięcie terminu</DialogTitle>
        <DialogContent>
          {currentSlot && (
            <DialogContentText>
              Czy na pewno chcesz usunąć termin <strong>{formatDate(currentSlot.date)}, {currentSlot.time}</strong>?
              {currentSlot.registered > 0 ? (
                <Typography color="error" sx={{ mt: 2 }}>
                  Uwaga: Ten termin ma już zarejestrowanych uczestników. Najpierw przenieś lub usuń uczestników.
                </Typography>
              ) : (
                <Typography sx={{ mt: 2 }}>
                  Ta operacja jest nieodwracalna.
                </Typography>
              )}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Anuluj</Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={loading || (currentSlot && currentSlot.registered > 0)}
          >
            {loading ? <CircularProgress size={24} /> : 'Usuń termin'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default SlotManagement;